// Import react elements
import { useRef, useState, useEffect } from 'react';



// Custom hook to support resizing panes horizontally or vertically
const useResizablePane = (initialSize = 300, side = 'right', orientation = 'horizontal') => {


    // Current size of the pane (width or height depending on orientation)
    const [paneSize, setPaneSize] = useState(initialSize);

    // Ref to the resizer DOM element
    const resizerRef = useRef(null);

    // Boolean ref to track whether resizing is active
    const isResizing = useRef(false);

    // Tracks initial X or Y coordinate on drag start
    const startCoord = useRef(0);

    // Tracks the size of the pane when drag begins
    const startSize = useRef(initialSize);


    useEffect(() => {

        // Called on mouse/touch move while dragging
        const handlePointerMove = (e) => {

            // Exit early if not resizing
            if (!isResizing.current) return;

            // Prevents text from being selected while dragging
            e.preventDefault();

            // Get current mouse/touch position
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;

            // Store the new size
            let newSize;

            // Horizontal resizing (left/right pane)
            if (orientation === 'horizontal') {

                if (side === 'right') {
                    const deltaX = startCoord.current - clientX;
                    newSize = startSize.current + deltaX;
                } else if (side === 'left') {
                    const deltaX = clientX - startCoord.current;
                    newSize = startSize.current + deltaX;
                } else {
                    return;
                }

            }

            // Vertical resizing (top/bottom stacking)
            else {

                if (side === 'bottom') {
                    const deltaY = clientY - startCoord.current;
                    newSize = startSize.current + deltaY;
                } else if (side === 'top') {
                    const deltaY = startCoord.current - clientY;
                    newSize = startSize.current + deltaY;
                } else {
                    return;
                }

            }

            // Clamp size between min/max values
            const clamped = Math.max(100, Math.min(newSize, 600));

            // Set pane size
            setPaneSize(clamped);

        };


        // Stop resizing - called when user releases mouse or touch
        const stopResizing = () => {

            isResizing.current = false;

            document.removeEventListener('mousemove', handlePointerMove);
            document.removeEventListener('mouseup', stopResizing);
            document.removeEventListener('touchmove', handlePointerMove);
            document.removeEventListener('touchend', stopResizing);

        };

        // Start resizing - called on mouse/touch down on the resizer
        const startResizing = (e) => {

            // Prevents text from being selected while dragging
            e.preventDefault();

            isResizing.current = true;

            // Capture initial pointer position
            startCoord.current = orientation === 'horizontal'
                ? (e.touches ? e.touches[0].clientX : e.clientX)
                : (e.touches ? e.touches[0].clientY : e.clientY);

            // Determine initial size of the resizable pane
            startSize.current = orientation === 'horizontal'
                ? (side === 'right'
                    ? resizerRef.current?.nextElementSibling?.offsetWidth
                    : resizerRef.current?.previousElementSibling?.offsetWidth) || paneSize
                : (side === 'bottom'
                    ? resizerRef.current?.nextElementSibling?.offsetHeight
                    : resizerRef.current?.previousElementSibling?.offsetHeight) || paneSize;

            // Attach listeners to track drag and release
            document.addEventListener('mousemove', handlePointerMove);
            document.addEventListener('mouseup', stopResizing);
            document.addEventListener('touchmove', handlePointerMove);
            document.addEventListener('touchend', stopResizing);
        };

        // Store the current ref value in a local variable to ensure consistent cleanup
        const refElement = resizerRef.current;

        // Add event listeners for mouse and touch interactions
        if (refElement) {

            refElement.addEventListener('mousedown', startResizing);
            refElement.addEventListener('touchstart', startResizing);

        }

        // Cleanup function to remove listeners when component unmounts or deps change
        return () => {

            if (refElement) {

                refElement.removeEventListener('mousedown', startResizing);
                refElement.removeEventListener('touchstart', startResizing);

            }

        };

        // Re run on pane size, size or orientation change
    }, [paneSize, side, orientation]);

    // Return the current pane size and the resizer ref for use in JSX
    return { paneSize, resizerRef };

};

export default useResizablePane;

